import React from 'react';
import { ListProps } from 'components/List';
import { MenuProps, MenuRef } from 'components/Menu';
import SelectItemLabelContent from './SelectItemLabelContent';
import SelectItemErrorMessage from './SelectItemErrorMessage';
import SelectListItem from './SelectListItem';
import SelectGroupHeading from './SelectGroupHeading';
import SelectItemButton from './SelectItemButton';
import SelectItemInput from './SelectItemInput';
import SelectItemBasic from './SelectItemBasic';
import SelectItemSearchable from './SelectItemSearchable';
import SelectItemText from './SelectItemText';
import SelectItemClearButton from './SelectItemClearButton';
import SelectItemProgressIndicator from './SelectItemProgressIndicator';
import SelectItemCreatedItem from './SelectItemCreatedItem';
import SelectItemBaseText from './SelectItemBaseText';
import SelectItemBaseIcon from './SelectItemBaseIcon';
import SelectItemSubText from './SelectItemSubText';
import SelectItemTextContainer from './SelectItemTextContainer';

export interface SelectItemProps extends React.ComponentPropsWithoutRef<'div'> {
  /** Enable searching (filtering) of the items. Also you need to use <SelectItemInput /> with `renderSelectItemBase` prop to enable searching, otherwise you can use <SelectItemButton/>. */
  searchable?: boolean;
  /** Enable creating of additional options into the list. Implies searchable (filtering) of the items (e.g. if creatable=true,
   * the value of the property searchable is ignored and the list will be searchable).
   */
  creatable?: boolean;
  /** Allow multiple selection of list items. */
  multiSelect?: boolean;
  /** The items in the dropdown List. Consider using SelectListItem, SelectGroupHeading, and SelectItemCreatedItem. */
  children: React.ReactNode;
  /** Controls dropdown List open/close state. */
  isOpen?: boolean;
  /** Function called when the dropdown List requests to be opened.
   * @param {object} event The event source of the callback.
   */
  onOpen?: (args: React.MouseEvent | React.KeyboardEvent) => void;
  /** Function called when dropdown List requests to be closed.
   * @param {object} event The event source of the callback.
   */
  onClose?: (args: React.MouseEvent | React.KeyboardEvent) => void;
  /** The style variant of the component. */
  variant?: 'outlined' | 'underlined';
  /** If `true`, the component will be disabled. Note: If the `disabled` prop is not set, it will be internally taken from the immediate parent `Label` component, if any. */
  disabled?: boolean;
  /** If `true`, the component will automatically get focus when the page loads. */
  autoFocus?: boolean;
  /**
    * Renders the items that are selected from the dropdown list and any dropdownn indicator icon into the SelectItem base area.
    * SelectItem can use SelectItemButton component for basic usage and SelectItemInput for searchable usage.
    * You can implement your own component to replace it. Please note that your component needs
    * to support and handle SelectItemButton/SelectItemInput props. Please refer to SelectItemButton/SelectItemInput for the property definitions.
    * e.g. props => <YourCustomItem {...props} />
  */
  renderSelectItemBase(...args: unknown[]): React.ReactNode;
  /** If `true`, the SelectItem will be rendered in the error state. */
  error?: boolean;
  /** If set, the errorMessage will be displayed in the error state message area. `error` property must be true for errorMessage to display.
    *  `Typography` component can be used to format the error message. */
  errorMessage?: React.ReactNode;
  /** If `true`, the max-width will be `31.25rem`. */
  maxWidth?: boolean;
  /** If `true`, the text in the dropdown list will be truncated with ellipsis if it exceeds the container width. Otherwise the List will have
   * a horizontal scroll bar. */
  truncateListText?: boolean;
  /** Dropdown List properties. See List component documentation for details. */
  listProps?: Partial<ListProps>;
  /** Menu properties.
   *
   * `usePortal`, Set to true to render menu in portal
   *
   * `portalContainer`, Specify container in which `menu` is rendered in portal.
   *
   * `positioningOptions`, Override `menu` positioning options based on popper (https://popper.js.org/docs/v2/).
   *
   *   positioningOptions: {
   *       placement: 'bottom',
   *        modifiers: [
   *            { name: 'offset', options: { offset: [0, 4] } },
   *         ],
   *       }
   */
  menuProps?: Partial<MenuProps>;
  /** Control animation of dropdown list. This uses React-spring transitions config. See https://react-spring.io for more information. */
  animationProps?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any,
}

export type SelectItemHandle = {
  selectItemRef: HTMLElement,
  menuRef: React.RefObject<MenuRef>,
  positionMenu: (any) => void
};

/** SelectItem is a form input element that is used for collecting user provided information from a list of options.
 * To re-position dropdown Menu on changes to children or target element, please use `positionMenu` callback using component ref.
 * The forwarded ref is available as 'selectItemRef'.
 * */
const SelectItem = React.forwardRef<SelectItemHandle, SelectItemProps>((props: SelectItemProps, ref) => {
  const { searchable, creatable } = props;
  let selectItemComponent = <SelectItemBasic ref={ref} {...props} />;
  if (searchable || creatable) {
    selectItemComponent = <SelectItemSearchable ref={ref} {...props} />;
  }
  return selectItemComponent;
});

SelectItem.defaultProps = {
  searchable: false,
  creatable: false,
  multiSelect: false,
  isOpen: false,
  variant: 'outlined',
  error: false,
  errorMessage: undefined,
  disabled: undefined, // `disabled`, by default, needs to be undefined to find is it configured by user or not.
  autoFocus: false,
  maxWidth: false,
  truncateListText: true,
  listProps: {},
  menuProps: {
    usePortal: true,
    portalContainer: document.body,
  },
  onOpen: undefined,
  onClose: undefined,
  animationProps: undefined,
  disableAnimation: undefined,
  theme: {},
};

SelectItem.displayName = 'SelectItem';

export {
  SelectItem as default,
  SelectItemLabelContent,
  SelectItemErrorMessage,
  SelectListItem,
  SelectGroupHeading,
  SelectItemButton,
  SelectItemInput,
  SelectItemBasic,
  SelectItemSearchable,
  SelectItemText,
  SelectItemClearButton,
  SelectItemProgressIndicator,
  SelectItemCreatedItem,
  SelectItemBaseText,
  SelectItemBaseIcon,
  SelectItemSubText,
  SelectItemTextContainer,
};
